/** Epic 6: Gig payroll — shared formulas (mirror SQL compute_dj_gig_payout). */

export type VatMode = 'inclusive' | 'exclusive';

export function customerTypeToVatMode(customerType: string | null | undefined): VatMode {
  return String(customerType ?? '').toLowerCase() === 'business' ? 'exclusive' : 'inclusive';
}

export function oreToKr(ore: number | null | undefined): number {
  return Math.round(Number(ore ?? 0)) / 100;
}

export function krToOre(kr: number): number {
  return Math.round(kr * 100);
}

export function formatOreAsKr(ore: number | null | undefined): string {
  const kr = oreToKr(ore);
  return `${kr.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} kr`;
}

export function computeDjGigPayout(
  grossOre: number,
  vatMode: VatMode,
  vatRatePercent = 25,
  kickbackPercent = 20,
): { feeBasisNetOre: number; kickbackNetOre: number; djPayoutNetOre: number } {
  if (!grossOre || grossOre <= 0) {
    return { feeBasisNetOre: 0, kickbackNetOre: 0, djPayoutNetOre: 0 };
  }
  const rate = vatRatePercent > 0 ? vatRatePercent : 25;
  const kick = kickbackPercent >= 0 ? kickbackPercent : 20;
  const feeBasisNetOre =
    vatMode === 'exclusive' ? grossOre : Math.round(grossOre / (1 + rate / 100));
  const kickbackNetOre = Math.round(feeBasisNetOre * (kick / 100));
  const djPayoutNetOre = Math.max(0, feeBasisNetOre - kickbackNetOre);
  return { feeBasisNetOre, kickbackNetOre, djPayoutNetOre };
}

export function vatModeLabel(mode: string | null | undefined): string {
  return mode === 'exclusive' ? 'exkl. moms' : 'inkl. moms';
}

/** Parsa timmar ur t.ex. "4 timmar", "2,5 h". */
export function parsePlayHoursFromDuration(duration: string | null | undefined): number | null {
  if (!duration?.trim()) return null;
  const match = duration.trim().match(/(\d+(?:[.,]\d+)?)/);
  if (!match) return null;
  const n = Number.parseFloat(match[1].replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export function formatPlayTime(
  playDuration: string | null | undefined,
  playHours: number | null | undefined,
): string {
  if (playDuration?.trim()) return playDuration.trim();
  if (playHours != null) {
    const rounded = Number.isInteger(playHours) ? String(playHours) : playHours.toLocaleString('sv-SE');
    return `${rounded} timmar`;
  }
  return '–';
}

export function formatKickbackPercent(value: number | null | undefined): string {
  const n = Number(value ?? 20);
  if (!Number.isFinite(n)) return '20 %';
  return `${Number.isInteger(n) ? n : n.toLocaleString('sv-SE')} %`;
}

export type GigAssignmentRow = {
  id: string;
  assignee_type: 'employee' | 'dj';
  employee_id?: string | null;
  employee_name?: string | null;
  dj_profile_id?: string | null;
  dj_name?: string | null;
  product_id?: string | null;
  product_name?: string | null;
  role: string;
  gig_date: string;
  pay_model: string;
  fixed_amount_ore?: number | null;
  hourly_rate_ore?: number | null;
  gig_fee_gross_ore?: number | null;
  vat_mode?: string | null;
  vat_rate?: number | null;
  fee_basis_net_ore?: number | null;
  kickback_percent?: number | null;
  kickback_net_ore?: number | null;
  dj_payout_net_ore?: number | null;
  status: string;
  notes?: string | null;
};

export type PayrollSummaryRow = {
  assignment_id: string;
  rental_request_id: string;
  order_number: string;
  assignee_type: string;
  assignee_name: string;
  role: string;
  gig_date: string;
  status: string;
  gig_fee_gross_ore?: number | null;
  vat_mode?: string | null;
  fee_basis_net_ore?: number | null;
  kickback_net_ore?: number | null;
  payable_ore: number;
};

export function assigneeDisplayName(row: GigAssignmentRow): string {
  if (row.assignee_type === 'dj') return row.dj_name || 'DJ';
  return row.employee_name || 'Anställd';
}

export function payableOreForAssignment(row: GigAssignmentRow): number {
  if (row.assignee_type === 'dj') return Number(row.dj_payout_net_ore ?? 0);
  return Number(row.fixed_amount_ore ?? 0);
}
